/**
 * AuthInitializer — runs the startup authentication flow exactly once.
 *
 * Lifecycle:
 *   1. Parse URL for authentication params
 *   2. If OAuth params → exchange authorization code (ignores stale localStorage)
 *   3. If persisted → restore session from storage
 *   4. Sync token into legacy stores (clientInfo, userSession)
 *   5. Initialize partner config
 *   6. Fetch user location
 *   7. Transition auth store to AUTHENTICATED / ERROR
 *
 * This component renders nothing — it is a pure side-effect boundary.
 *
 * Replaces:
 *  - InitApplicationAuthState component
 *  - usePartnerAuth hook
 *  - useAuthLifecycle hook
 *  - usePartnerConfig hook (initialization part)
 */

import { useCallback, useEffect, useRef } from "react"
import { useFetchLocation } from "@/common/queries/use-fetch-location"
import { parseSessionInvoiceFromSearchParams } from "@/invoice/session-invoice"
import { useSearchParams } from "react-router-dom"

import { APP_VERSION } from "@be-miniapp/app-configs"
import { log, setMonitorUser, trackJourney } from "@be-miniapp/logging"
import { hasResolvedRideLocation } from "@be-miniapp/shared"
import { useInitializationStatus } from "@be-miniapp/shared/stores/initialized"
import { useLocationPopupSetting } from "@be-miniapp/shared/stores/location-popup"
import type { Location } from "@be-miniapp/types"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { useClientInfo } from "@/store/client-info"
import { usePartnerInfo } from "@/store/partner-information"
import { useSessionInvoiceStore } from "@/store/session-invoice"
import { useUserPersistingData } from "@/store/user-persisting-data"
import { useUserStore } from "@/store/user-session"
import {
  hydratePersistedLocationToRuntime,
  waitForUserPersistingDataHydration,
} from "@/utils/hydrate-persisted-location"
import {
  initializeNativeLocationBridge,
  isNativeLocationBridgeAvailable,
  registerNativeLocationGrantHandler,
} from "@/utils/native-location-permission"

initializeNativeLocationBridge()

import {
  AUTH_ERROR_CODE,
  AUTH_PARAM_WAIT_TIMEOUT_MS,
  getUnauthorizedPath,
  type AuthErrorCode,
} from "@/constants/auth-error"
import {
  DEFAULT_PARTNER,
  getPartnerSourceApp,
  validatePartnerIdentifier,
  type AllowedPartner,
} from "@/constants/partner"

import {
  recordAuthFailure,
  recordAuthOutcome,
  recordAuthStep,
  runAuthInitializationSpan,
  summarizeAuthParams,
  summarizeSearchParams,
} from "./auth-instrumentation"
import { useAuthStore } from "./auth-store"
import { AUTH_STATUS } from "./auth-types"
import {
  buildDevAuthUrl,
  getDevCredentials,
  isDevMode,
  saveDevCredentials,
} from "./dev-auth-helper"
import {
  buildSession,
  clearPersistedSession,
  exchangeAuthorizationCode,
  parseAuthenticationParams,
  persistSession,
  restorePersistedSession,
} from "./oauth-handler"

// ---------------------------------------------------------------------------
// Helper: parse location from URL params
// ---------------------------------------------------------------------------

function parseLocationFromSearchParams(
  searchParams: URLSearchParams
): Location | null {
  const latitude = searchParams.get("latitude")
  const longitude = searchParams.get("longitude")
  if (!latitude || !longitude) return null

  const lat = parseFloat(latitude)
  const lng = parseFloat(longitude)
  if (
    Number.isNaN(lat) ||
    Number.isNaN(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  )
    return null

  return { latitude: lat, longitude: lng }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function AuthInitializer() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const hasStartedRef = useRef(false)
  const hasParsedSessionInvoiceRef = useRef(false)
  const missingParamsTimerRef = useRef<number | null>(null)

  // Auth store actions
  const status = useAuthStore((s) => s.status)
  const startSessionRestore = useAuthStore((s) => s.startSessionRestore)
  const startAuthentication = useAuthStore((s) => s.startAuthentication)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)
  const setError = useAuthStore((s) => s.setError)

  // Legacy store actions (kept for backward compatibility during migration)
  const setVersion = useClientInfo((s) => s.setVersion)
  const setAccessToken = useClientInfo((s) => s.setAccessToken)
  const setDeviceToken = useClientInfo((s) => s.setDeviceToken)
  const setSourceApp = useClientInfo((s) => s.setSourceApp)
  const setLatestToken = useUserStore((s) => s.setToken)
  const setPartnerIdentifier = usePartnerInfo((s) => s.setPartnerIdentifier)
  const showPermissionDenied = useLocationPopupSetting(
    (s) => s.showPermissionDenied
  )
  const showServiceUnavailable = useLocationPopupSetting(
    (s) => s.showServiceUnavailable
  )
  const dismissLocationPopup = useLocationPopupSetting((s) => s.dismiss)

  // Location
  const providedLocation = parseLocationFromSearchParams(searchParams)
  const { fetch: fetchLocation } = useFetchLocation({
    options: {
      onDenied: () => {
        const persisted = useUserPersistingData.getState()
        if (
          hasResolvedRideLocation({
            location: persisted.location,
            address: persisted.locationAddress,
            isLocationManuallyChanged: persisted.isLocationManuallyChanged,
            lastLocationTimestamp: persisted.lastLocationTimestamp,
            locationPermissionStatus: persisted.locationPermissionState.status,
          })
        ) {
          hydratePersistedLocationToRuntime()
          dismissLocationPopup()
          return
        }

        showPermissionDenied()
        log({
          category: "location-permission",
          level: "info",
          message: "Location permission denied",
        })
      },
      onAccepted: () => {
        dismissLocationPopup()
      },
      onUnAvailable: (message?: string) => {
        if (isNativeLocationBridgeAvailable()) {
          log({
            category: "location-permission",
            level: "info",
            message:
              "Deferring location popup while native host resolves permission",
            data: { message },
          })
          return
        }

        showServiceUnavailable()
        log({
          category: "location-permission",
          level: "error",
          message: `Location permission unavailable: ${message}`,
        })
      },
    },
    providedLocation,
  })

  // Legacy initialization status store (backward compat for shared packages)
  const setLegacyInitialized = useInitializationStatus(
    (s) => s.setIsInitialized
  )

  // ---------------------------------------------------------------------------
  // Sync token into legacy stores so axios interceptors & existing code work
  // ---------------------------------------------------------------------------
  const syncTokenToLegacyStores = useCallback(
    (accessToken: string, deviceId?: string) => {
      setAccessToken(accessToken)
      setLatestToken(accessToken)
      if (deviceId) setDeviceToken(deviceId)
    },
    [setAccessToken, setLatestToken, setDeviceToken]
  )

  // ---------------------------------------------------------------------------
  // Initialize partner configuration
  // ---------------------------------------------------------------------------
  const initializePartner = useCallback((): string => {
    const rawIdentifier =
      searchParams.get("ptr") ||
      localStorage.getItem("partner_identifier") ||
      DEFAULT_PARTNER

    const validated = validatePartnerIdentifier(rawIdentifier)
    setPartnerIdentifier(validated as AllowedPartner)
    setSourceApp(getPartnerSourceApp(validated))
    localStorage.setItem("partner_identifier", validated)

    return validated
  }, [searchParams, setPartnerIdentifier, setSourceApp])

  // ---------------------------------------------------------------------------
  // Fetch location (non-blocking — errors are logged, not thrown)
  // ---------------------------------------------------------------------------
  const loadUserLocation = useCallback(async () => {
    try {
      await waitForUserPersistingDataHydration()
      if (hydratePersistedLocationToRuntime()) {
        dismissLocationPopup()
        return
      }

      await fetchLocation()
    } catch (error) {
      log({
        category: "location-permission",
        level: "error",
        message: "Failed to fetch location during auth initialization",
        data: { error },
      })
    }
  }, [dismissLocationPopup, fetchLocation])

  // ---------------------------------------------------------------------------
  // Dev mode: Auto-restore session without requiring URL params
  // ---------------------------------------------------------------------------
  const tryAutoRestoreSession = useCallback(async (): Promise<boolean> => {
    // Only auto-restore in development mode
    if (!isDevMode()) return false

    // Try 1: Restore from persisted token
    const restoredToken = restorePersistedSession()
    if (restoredToken) {
      const partnerIdentifier = initializePartner()
      startSessionRestore()
      syncTokenToLegacyStores(restoredToken.accessToken, restoredToken.deviceId)
      const session = buildSession(restoredToken, partnerIdentifier)

      await loadUserLocation()
      setAuthenticated(session)
      setLegacyInitialized(true, true)
      setMonitorUser({ id: session.token.deviceId ?? "restored-session" })
      trackJourney("autologin", "success", { strategy: "dev_restore" })
      recordAuthStep("init.complete", { strategy: "dev_restore" })
      recordAuthOutcome("success", { strategy: "dev_restore" })

      log({
        category: "auth",
        level: "info",
        message: "Dev mode: Session auto-restored from localStorage",
      })
      return true
    }

    // Try 2: Redirect with saved dev credentials (if available)
    const devCreds = getDevCredentials()
    if (devCreds) {
      log({
        category: "auth",
        level: "info",
        message: "Dev mode: Redirecting with saved credentials",
        data: { phoneNo: devCreds.phoneNo },
      })

      // Build URL with saved credentials and reload
      const authUrl = buildDevAuthUrl()
      window.location.href = authUrl
      return true // Prevent further processing
    }

    return false
  }, [
    initializePartner,
    startSessionRestore,
    syncTokenToLegacyStores,
    loadUserLocation,
    setAuthenticated,
    setLegacyInitialized,
  ])

  // ---------------------------------------------------------------------------
  // Main initialization flow
  // ---------------------------------------------------------------------------
  const runInitialization = useCallback(async () => {
    return runAuthInitializationSpan(
      summarizeSearchParams(searchParams),
      async () => {
    try {
      recordAuthStep("init.start", summarizeSearchParams(searchParams))
      trackJourney("autologin", "start")

      // Step 1: Set app version
      setVersion(APP_VERSION)

      // Step 2: Initialize partner config
      const partnerIdentifier = initializePartner()
      recordAuthStep("init.partner_configured", { partnerIdentifier })

      // Step 3: Determine authentication strategy
      const authParams = parseAuthenticationParams(searchParams)
      recordAuthStep("init.params_parsed", summarizeAuthParams(authParams))

      // Step 3a: Dev mode - try auto-restore from localStorage (handles HMR reloads)
      if (authParams.kind === "none") {
        recordAuthStep("init.waiting_for_params", summarizeSearchParams(searchParams))
        const autoRestored = await tryAutoRestoreSession()
        if (autoRestored) return

        // No auth params and no persisted token — wait for webview to append them
        // The effect will re-run when searchParams change
        return
      }

      if (authParams.kind === "provider-error") {
        const providerError = {
          message:
            authParams.params.description ||
            authParams.params.error ||
            "Partner authorization was denied",
          code: AUTH_ERROR_CODE.PROVIDER_DENIED,
          cause: authParams.params,
        }

        recordAuthStep("init.provider_error", summarizeAuthParams(authParams), "warning")
        log({
          category: "auth",
          level: "warning",
          message: "Provider returned an authorization error",
          data: { error: authParams.params.error },
        })

        recordAuthFailure(providerError, {
          step: "init.provider_error",
          code: providerError.code,
          strategy: authParams.kind,
          extra: summarizeAuthParams(authParams),
        })

        setError(providerError)
        setLegacyInitialized(true, false)
        trackJourney("autologin", "fail", { code: providerError.code })
        navigate(getUnauthorizedPath(providerError.code), { replace: true })
        return
      }

      // Step 4: Execute the appropriate auth strategy
      if (authParams.kind === "persisted") {
        startSessionRestore()
        recordAuthStep("init.persisted_restore", { partnerIdentifier })

        const restoredToken = restorePersistedSession()
        if (restoredToken) {
          syncTokenToLegacyStores(
            restoredToken.accessToken,
            restoredToken.deviceId
          )
          const session = buildSession(restoredToken, partnerIdentifier)
          recordAuthStep("init.location_fetch")
          await loadUserLocation()
          setAuthenticated(session)
          setLegacyInitialized(true, true)
          setMonitorUser({ id: session.token.deviceId ?? "restored-session" })

          trackJourney("autologin", "success", { strategy: "persisted" })
          recordAuthStep("init.complete", { strategy: "persisted" })
          recordAuthOutcome("success", { strategy: "persisted" })

          log({
            category: "auth",
            level: "info",
            message: "Session restored from persisted storage",
          })
          return
        }

        // Persisted flag was set but no valid token found — fall through to error
        const persistedError = {
          message:
            "Persisted authentication was requested but no valid token was found",
          code: AUTH_ERROR_CODE.PERSISTED_TOKEN_INVALID,
        }

        recordAuthStep("init.persisted_invalid", undefined, "warning")
        log({
          category: "auth",
          level: "warning",
          message: "Persisted flag set but no valid token found",
        })

        recordAuthFailure(persistedError, {
          step: "init.persisted_invalid",
          code: persistedError.code,
          strategy: authParams.kind,
        })

        setError(persistedError)
        setLegacyInitialized(true, false)
        trackJourney("autologin", "fail", { code: persistedError.code })
        navigate(getUnauthorizedPath(persistedError.code), { replace: true })
        return
      }

      // OAuth or internal-testing flow
      startAuthentication()
      recordAuthStep("init.token_exchange", {
        strategy: authParams.kind,
        partnerIdentifier,
      })

      if (
        authParams.kind === "oauth" ||
        authParams.kind === "internal-testing"
      ) {
        clearPersistedSession()
        setAccessToken(undefined)
        setLatestToken(null)
      }

      const authToken = await exchangeAuthorizationCode()
      syncTokenToLegacyStores(authToken.accessToken, authToken.deviceId)
      persistSession(authToken)
      recordAuthStep("init.session_persisted", {
        strategy: authParams.kind,
        hasDeviceId: Boolean(authToken.deviceId),
      })

      // Dev mode: Save credentials for HMR persistence
      if (isDevMode() && authParams.kind === "internal-testing") {
        saveDevCredentials({
          phoneNo: authParams.params.phoneNo,
          partnerId: authParams.params.partnerId,
          partnerSecret: authParams.params.partnerSecret,
          fullname: authParams.params.fullname,
          ptr: partnerIdentifier,
        })
      }

      const session = buildSession(authToken, partnerIdentifier)
      recordAuthStep("init.location_fetch")
      await loadUserLocation()
      setAuthenticated(session)
      setLegacyInitialized(true, true)
      setMonitorUser({ id: session.token.deviceId ?? "authenticated-session" })

      trackJourney("autologin", "success", { strategy: authParams.kind })
      recordAuthStep("init.complete", { strategy: authParams.kind })
      recordAuthOutcome("success", {
        strategy: authParams.kind,
        partnerIdentifier,
      })

      log({
        category: "auth",
        level: "info",
        message: "Authentication completed successfully",
        data: { partnerIdentifier },
      })
    } catch (error) {
      const authParams = parseAuthenticationParams(searchParams)
      const authError = {
        message:
          error instanceof Error
            ? error.message
            : "Unknown authentication error",
        code:
          ((error as Record<string, unknown>)?.code as
            | AuthErrorCode
            | undefined) || AUTH_ERROR_CODE.TOKEN_EXCHANGE_FAILED,
        cause: error,
      }

      recordAuthFailure(error, {
        step: "init.token_exchange",
        code: authError.code,
        strategy: authParams.kind === "none" ? undefined : authParams.kind,
        extra: summarizeAuthParams(authParams),
      })

      log({
        category: "auth",
        level: "error",
        message: "Authentication failed",
        data: { error: { message: authError.message, code: authError.code } },
      })

      trackJourney("autologin", "fail", { code: authError.code })

      setError(authError)
      setLegacyInitialized(true, false)
      navigate(getUnauthorizedPath(authError.code), { replace: true })
    }
      }
    )
  }, [
    setVersion,
    initializePartner,
    searchParams,
    startSessionRestore,
    syncTokenToLegacyStores,
    loadUserLocation,
    setAuthenticated,
    startAuthentication,
    setError,
    setLegacyInitialized,
    navigate,
    tryAutoRestoreSession,
    setAccessToken,
    setLatestToken,
  ])

  useEffect(() => {
    return registerNativeLocationGrantHandler(() => {
      void loadUserLocation()
    })
  }, [loadUserLocation])

  // ---------------------------------------------------------------------------
  // Effect 0: Parse session invoice params once on launch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (hasParsedSessionInvoiceRef.current) return
    hasParsedSessionInvoiceRef.current = true

    const sessionInvoice = parseSessionInvoiceFromSearchParams(searchParams)
    if (!sessionInvoice) return

    useSessionInvoiceStore.getState().setSessionInvoice(sessionInvoice)

    log({
      category: "session-invoice",
      level: "info",
      message: "Session invoice params captured from launch URL",
      data: {
        company_name: sessionInvoice.company_name,
        company_tax_no: sessionInvoice.company_tax_no,
      },
    })
  }, [searchParams])

  // ---------------------------------------------------------------------------
  // Effect 1: Dev mode auto-restore on mount (handles direct navigation without auth params)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Only run in dev mode when uninitialized
    if (!isDevMode()) return
    if (status !== AUTH_STATUS.UNINITIALIZED) return
    if (hasStartedRef.current) return

    // Check if there are already auth params (if so, let Effect 2 handle it)
    const authParams = parseAuthenticationParams(searchParams)
    if (authParams.kind !== "none") return

    // Try auto-restore from token or redirect with saved creds
    hasStartedRef.current = true
    tryAutoRestoreSession().then((restored) => {
      if (!restored) {
        // No token and no saved creds - allow re-run
        hasStartedRef.current = false
      }
    })
  }, [status, searchParams, tryAutoRestoreSession])

  // ---------------------------------------------------------------------------
  // Effect 2: Run initialization when auth params become available
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Only run when we're in the uninitialized state
    if (status !== AUTH_STATUS.UNINITIALIZED) return

    // Check if we have auth params to work with
    const authParams = parseAuthenticationParams(searchParams)
    if (authParams.kind === "none") return

    // Prevent concurrent runs
    if (hasStartedRef.current) return
    hasStartedRef.current = true

    runInitialization().finally(() => {
      // Allow re-run if initialization didn't transition out of UNINITIALIZED
      // (e.g. if runInitialization returned early)
      if (useAuthStore.getState().status === AUTH_STATUS.UNINITIALIZED) {
        hasStartedRef.current = false
      }
    })
  }, [status, searchParams, runInitialization])

  useEffect(() => {
    if (isDevMode()) return
    if (status !== AUTH_STATUS.UNINITIALIZED) return

    const authParams = parseAuthenticationParams(searchParams)
    if (authParams.kind !== "none") return

    if (missingParamsTimerRef.current) {
      window.clearTimeout(missingParamsTimerRef.current)
    }

    missingParamsTimerRef.current = window.setTimeout(() => {
      const missingParamsError = {
        message: "No auth parameters were provided by the partner container",
        code: AUTH_ERROR_CODE.MISSING_AUTH_PARAMS,
      }

      recordAuthFailure(missingParamsError, {
        step: "init.missing_params_timeout",
        code: missingParamsError.code,
        extra: summarizeSearchParams(searchParams),
      })

      log({
        category: "auth",
        level: "error",
        message: "Authentication parameters were not provided in time",
      })

      trackJourney("autologin", "fail", { code: missingParamsError.code })

      setError(missingParamsError)
      setLegacyInitialized(true, false)
      navigate(getUnauthorizedPath(missingParamsError.code), { replace: true })
    }, AUTH_PARAM_WAIT_TIMEOUT_MS)

    return () => {
      if (missingParamsTimerRef.current) {
        window.clearTimeout(missingParamsTimerRef.current)
        missingParamsTimerRef.current = null
      }
    }
  }, [navigate, searchParams, setError, setLegacyInitialized, status])

  return null
}

AuthInitializer.displayName = "AuthInitializer"

export { AuthInitializer }
